"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { Post } from "@/types";
import CommentList from "../comment/CommentList";
import CommentInput from "../comment/CommentInput";
import ImagePreview from "../ImagePreview";
import { useComment } from "@/hooks/useComment";
import useLogin from "@/hooks/useLogin";
import useDocStore from "@/models/doc";
import { likeDocService, unLikeDocService } from "@/services/post";
import { followUserService, unfollowUserService } from "@/services/user";
import { useStatus } from "@/hooks/useStatus";
import { useSwipe } from "@/hooks/useSwipe";
import { track } from "@/lib/log";
import clsx from "clsx";
import ImageList from "@/components/mobile/ImageList"; // 新增引入

interface MobilePostPageProps {
    post: Post & { up: number, isLiked: boolean, isNowLiked: boolean };
    onBack?: () => void;
    onLike?: (post: Post & { isNowLiked?: boolean, isLiked?: boolean }) => void;
    qualityComments?: any[];
}

export default function MobilePostPage({ post, onBack, onLike, qualityComments = [] }: MobilePostPageProps) {
    const router = useRouter();
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [likeCount, setLikeCount] = useState(post?.up || 0);
    const [isNowLiked, setIsNowLiked] = useState<boolean>(post?.isNowLiked || false);
    const [isFollowed, setIsFollowed] = useState(post?.followed == 1);
    const [previewOpen, setPreviewOpen] = useState(false);

    const { setDoc } = useDocStore();
    const { checkLogin } = useLogin();
    const { liked, toggleLiked } = useStatus({ docid: post?.docid, media_id: post?.media_id });
    const { postComment } = useComment(post?.docid!);

    // 点赞数计算
    function changeLikeCount(isLiked: boolean, isNowLiked: boolean, up: number) {
        setLikeCount(() => {
            let res = up;
            if (isLiked && !isNowLiked) {
                res = up - 1;
            } else if (!isLiked && isNowLiked) {
                res = up + 1;
            } else {
                res = up;
            }
            return res;
        });
    }

    useEffect(() => {
        changeLikeCount(post?.isLiked || false, isNowLiked, post?.up || 0);
    }, [isNowLiked, post?.isLiked, post?.up]);

    useEffect(() => {
        if (post) {
            setDoc({
                docid: post.doc_id,
                title: post.title,
                content: post.content,
                authorId: post.author.id,
                tags: post.tags,
                images: post.images
            });

            // 设置页面标题
            if (post.title) {
                document.title = post.title;
            }
        }
    }, [post]);

    // 图片切换
    const handlePrevImage = () => {
        if (!post) return;
        setCurrentImageIndex((prev) => Math.max(0, prev - 1));
    };

    const handleNextImage = () => {
        if (!post) return;
        setCurrentImageIndex((prev) => Math.min(post.images.length - 1, prev + 1));
    };

    // 滑动处理
    const { handlers: swipeHandlers } = useSwipe({
        onSwipeLeft: () => {
            if (post && currentImageIndex < post.images.length - 1) {
                handleNextImage();
                track("switch_image", {
                    action: "next_image",
                    doc_id: post?.docid,
                    type: "swipe",
                    page: "mobile_detail"
                });
            }
        },
        onSwipeRight: () => {
            if (currentImageIndex > 0) {
                handlePrevImage();
                track("switch_image", {
                    action: "prev_image",
                    doc_id: post?.docid,
                    type: "swipe",
                    page: "mobile_detail"
                });
            }
        },
        threshold: 50,
    });

    // 点赞处理
    const handleLike = checkLogin(() => {
        setIsNowLiked(e => !e);
        toggleLiked(post?.docid!);
        if (liked) {
            unLikeDocService({ docid: post?.docid });
        } else {
            likeDocService({ docid: post?.docid });
        }
    });
    // 同步 isNowLiked 状态
    useEffect(() => {
        if (post) {
            setIsNowLiked(post.isNowLiked || false);
        }
    }, [post]);
    // 关注处理
    useEffect(() => {
        if (post) {
            setIsFollowed(post.followed == 1);
        }
    }, [post]);
    const handleFollow = checkLogin(() => {
        if (!post) return;
        track("follow_user", {
            action: isFollowed ? "unfollow" : "follow",
            doc_id: post?.docid,
            media_id: post?.media_id,
            type: "click"
        });
        setIsFollowed(!isFollowed);
        if (isFollowed) {
            unfollowUserService(post?.media_id);
        } else {
            followUserService(post?.media_id);
        }
    });

    // 状态改变时调用onLike回调，使用useRef避免不必要的重复调用
    const prevStateRef = useRef<{ isFollowed: boolean; isNowLiked: boolean }>({
        isFollowed: isFollowed,
        isNowLiked: isNowLiked
    });

    useEffect(() => {
        if (post && (prevStateRef.current.isFollowed !== isFollowed || prevStateRef.current.isNowLiked !== isNowLiked)) {
            onLike?.({ ...post, followed: isFollowed ? 1 : 0, isNowLiked } as Post);
            prevStateRef.current = { isFollowed, isNowLiked };
        }
    }, [isFollowed, isNowLiked, post, onLike]);

    // 组件卸载时同步状态到父组件（类似于ClientPostDetail中modal关闭时的逻辑）
    useEffect(() => {
        return () => {
            if (post) {
                onLike?.({ ...post, followed: isFollowed ? 1 : 0, isNowLiked } as Post);
            }
        };
    }, [post, isFollowed, isNowLiked, onLike]);

    const handleBack = () => {
        onBack ? onBack() : router.push("/")
    };
    function gotoComment() {
        // router.push(`/posts/${post.docid}?redirected=comment`);
        const commentSection = document.getElementById("comment-section");
        if (commentSection) {
            commentSection.scrollIntoView({ behavior: "smooth" });
        }
    }
    if (!post) return null;

    // 移除原有图片高度和图片区域相关逻辑，直接用ImageList组件
    return (
        <div className="min-h-screen min-w-screen w-screen h-screen fixed top-0 right-0 z-50 bg-white overflow-y-auto pb-10">
            {/* 顶部导航栏 */}
            <div className="sticky top-0 z-50 bg-white border-b border-gray-200 flex justify-between items-center px-4 py-3">
                <div className="flex items-center justify-between">
                    <button
                        onClick={handleBack}
                        className="p-2 -ml-2 text-gray-600 hover:text-gray-900 transition-colors"
                        tabIndex={0}
                        aria-label="back to home"
                        onKeyDown={e => {
                            if (e.key === "Enter" || e.key === " ") handleBack();
                        }}
                    >
                        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    <div className="flex items-center space-x-3">
                        <img
                            src={post.author.avatar}
                            alt={post.author.username}
                            className="w-9 h-9 rounded-full object-cover"
                        />
                        <a href={`/users/${post.media_id}`} target="_blank" className="font-medium text-gray-900">{post.author.username}</a>
                    </div>

                </div>
                <button
                    onClick={handleFollow}
                    className={clsx(
                        "px-4 py-1 text-sm rounded-full transition-colors",
                        isFollowed
                            ? "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            : "bg-primary text-black hover:bg-primary/80"
                    )}
                >
                    {isFollowed ? "Following" : "Follow"}
                </button>
            </div>

            {/* 图片区域，使用ImageList组件 */}
            {post.images && post.images.length > 0 && (
                <ImageList
                    images={post.images}
                    title={post.title}
                    ratio={post.coverRatio}
                />
            )}

            {/* 内容区域 */}
            <div className="px-4 py-4">
                {/* 标题 */}
                <h1 className="text-xl font-semibold text-gray-900 mb-3">
                    {post.title}
                </h1>

                {/* 内容 */}
                <div className="text-gray-700 text-base leading-relaxed whitespace-pre-line mb-4">
                    <ContentRender content={post.content} tags={post.tags} />
                </div>

                {/* 位置和时间 */}
                <div className="flex items-center space-x-1 text-sm text-gray-500 mb-4">
                    {post.city_name && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                    )}
                    <span>{post.city_name && `${post.city_name} • `}{post.date_str}</span>
                </div>

                {/* 操作按钮 */}
            </div>

            {/* 评论区域 */}
            <div className="px-4 pb-20" id="comment-section">
                <CommentList length={post.comments} doc_id={post.docid} qualityComments={qualityComments} />
            </div>

            {/* 底部评论输入 */}
            <div className="fixed left-0 right-0 bottom-0">
                <CommentInput postComment={postComment} isMobile={true} slot={
                    <div className="flex items-center space-x-3 pr-3">
                        <button
                            onClick={handleLike}
                            className={clsx(
                                "flex items-center space-x-2 text-sm transition-colors",
                                isNowLiked ? "text-red-500" : "text-gray-500 hover:text-red-500"
                            )}
                        >
                            <svg
                                className={clsx("w-6 h-6", isNowLiked ? "fill-current" : "fill-none")}
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                                />
                            </svg>
                            <span>{likeCount}</span>
                        </button>

                        <div className="flex items-center space-x-2 text-sm text-gray-500" onClick={() => {
                            gotoComment()
                        }}>
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                                />
                            </svg>
                            <span>{post.comments}</span>
                        </div>
                    </div>
                } />
            </div>

            {/* 图片预览 */}
            <ImagePreview
                isMobile={true}
                src={post.images[currentImageIndex]}
                isOpen={previewOpen}
                onOpenChange={setPreviewOpen}
            />
        </div>
    );
}

// 内容渲染组件（复用原来的逻辑）
const ContentRender = ({ content, tags = [] }: { content: string, tags: { id: string, name: string }[] }) => {
    const renderContentWithTags = () => {
        if (!content || !tags || tags.length === 0) {
            return content;
        }

        const tagPattern = new RegExp(
            tags
                .map((tag) => {
                    const tagName = tag.name.startsWith("#") ? tag.name : `#${tag.name}`;
                    return tagName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
                })
                .join("|"),
            "g"
        );

        const parts = [];
        let lastIndex = 0;
        let match;
        let idx = 0;

        while ((match = tagPattern.exec(content)) !== null) {
            const { index } = match;
            if (index > lastIndex) {
                parts.push(content.slice(lastIndex, index));
            }
            const tagText = match[0];
            const tag = tags.find((t) => t.name === tagText.replace(/^#/, ""));
            parts.push(
                <a
                    target="_blank"
                    key={`tag-${tagText}-${idx++}`}
                    href={`/tag/${encodeURIComponent(tag?.name || "")}`}
                    className="text-blue-500 hover:underline cursor-pointer"
                    tabIndex={0}
                    aria-label={`label: ${tag?.name}`}
                >
                    {tagText}
                </a>
            );
            lastIndex = index + tagText.length;
        }

        if (lastIndex < content.length) {
            parts.push(content.slice(lastIndex));
        }

        return parts;
    };

    return (
        <div className="post-content">
            {renderContentWithTags()}
        </div>
    );
} 